import { pickExtendedDealApiRoomName } from "../../features/corporate/package/rooms/mapCorporateExtendedDealApiRoom";
import { normalizeRoomTypeLabel } from "../vacationPackageRoomPricingSSOT";
import {
  canonicalSearchCardRoomForSlot,
  filterAvailableRoomsForSlotOccupancy,
  hasCanonicalSearchCardRoom,
  isRoomRowOccupancyCompatible,
  matchCanonicalRoomInAvailableRows,
  readCanonicalSearchCardRoom,
  SEARCH_CARD_ROOM_FIELD,
} from "./resolveSearchCardRoom";

function pushUniqueLabel(out, value) {
  const s = String(value ?? "").trim().toUpperCase();
  if (!s || out.includes(s)) return;
  out.push(s);
}

function roomTypeLabelFromSuggestion(suggestion) {
  return (
    normalizeRoomTypeLabel(suggestion) ||
    String(pickExtendedDealApiRoomName(suggestion) || "")
      .trim()
      .toUpperCase() ||
    null
  );
}

/**
 * Room type the agent selected in search (card/row), before extended-matrix init.
 * HotelsRiver often keeps this on hotel.roomType / search roomsAndPaxInfo while
 * passengersInfo was still empty — and then auto-init used to force cheapest.
 */
export function resolvePreferredRoomTypeLabelsFromSelectedResult(selectedResult) {
  const labels = [];
  if (!selectedResult || typeof selectedResult !== "object") return labels;

  const hotel = selectedResult.hotel || selectedResult.fullDeal?.hotel || {};
  const searchCardRoom = readCanonicalSearchCardRoom(selectedResult);
  pushUniqueLabel(labels, searchCardRoom?.roomType);
  pushUniqueLabel(labels, searchCardRoom?.roomTypeName);
  pushUniqueLabel(labels, selectedResult.selectedRoom?.roomType);
  pushUniqueLabel(labels, selectedResult.selectedRoom?.roomTypeName);
  pushUniqueLabel(labels, selectedResult.selectedRoom?.roomName);
  pushUniqueLabel(labels, selectedResult.roomType);
  pushUniqueLabel(labels, selectedResult.roomTypeName);
  pushUniqueLabel(labels, hotel.roomType);
  pushUniqueLabel(labels, hotel.room_type);
  pushUniqueLabel(labels, hotel.roomTypeName);
  pushUniqueLabel(labels, hotel.RoomType);

  const paxSources = [
    selectedResult.roomsAndPaxInfo,
    selectedResult.fullDeal?.roomsAndPaxInfo,
  ];
  for (const pax of paxSources) {
    if (!Array.isArray(pax)) continue;
    for (const slot of pax) {
      pushUniqueLabel(labels, slot?.room?.roomType);
      pushUniqueLabel(labels, slot?.room?.roomTypeName);
      pushUniqueLabel(labels, slot?.room?.roomName);
      pushUniqueLabel(labels, slot?.roomType);
      pushUniqueLabel(labels, slot?.roomName);
    }
  }

  const hotelName = String(hotel.hotelName || hotel.name || "").trim();
  if (hotelName && /[–—-]/.test(hotelName)) {
    const parts = hotelName.split(/\s*[–—-]\s*/).filter(Boolean);
    if (parts.length >= 2) pushUniqueLabel(labels, parts[parts.length - 1]);
  }

  return labels;
}

function resolveSearchAdultFare(selectedResult) {
  if (!selectedResult || typeof selectedResult !== "object") return null;
  const searchCardRoom = readCanonicalSearchCardRoom(selectedResult);
  const candidates = [
    searchCardRoom?.adultFare,
    selectedResult.selectedRoom?.adultFare,
    selectedResult.dealFare?.adultFare,
    selectedResult.fullDeal?.dealFare?.adultFare,
    selectedResult.adultFare,
    selectedResult.fullDeal?.adultFare,
  ];
  for (const raw of candidates) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/**
 * Resolve the extended-matrix row that matches the agent search selection.
 * Never falls back to cheapest / first row — returns null when identity is unknown or unmatched.
 * @param {Array<{ roomSuggestion?: object, ourFareDetails?: object, ldsDealId?: any }>} availableRooms
 * @param {object|null|undefined} selectedResult
 * @param {object|null|undefined} [slotRoom] current passengersInfo slot.room (may already hold roomType)
 * @returns {object|null}
 */
export function pickAvailableRoomMatchingSearchSelection(
  availableRooms,
  selectedResult,
  slotRoom = null,
  slotIndex = 0,
  occupancySlot = null
) {
  if (!Array.isArray(availableRooms) || availableRooms.length === 0) return null;

  const pool = occupancySlot
    ? filterAvailableRoomsForSlotOccupancy(availableRooms, occupancySlot)
    : availableRooms;
  if (pool.length === 0) return null;

  const rootCanonical = readCanonicalSearchCardRoom(selectedResult);
  const canonical = canonicalSearchCardRoomForSlot(rootCanonical, slotIndex);
  const slotHasCanonical = hasCanonicalSearchCardRoom({
    [SEARCH_CARD_ROOM_FIELD]: canonical,
  });
  if (slotHasCanonical) {
    const canonicalHit = matchCanonicalRoomInAvailableRows(
      pool,
      canonical,
      occupancySlot
    );
    if (canonicalHit) return canonicalHit;
    // Search-card SKU exists but is not in this matrix. Do not fall through to
    // hotel.roomType / dealFare — that silently switches to another room.
    return null;
  }
  // Multi-room: missing slot identity must not inherit slot 0's STANDARD ROOM
  // label and then pick the cheapest same-name A1 SKU.
  if (hasCanonicalSearchCardRoom(selectedResult) && slotIndex > 0) {
    return null;
  }
  if (slotRoom && typeof slotRoom === "object") {
    const slotCanonicalHit = matchCanonicalRoomInAvailableRows(
      pool,
      {
        seqNum: slotRoom["@seqNum"] ?? slotRoom.seqNum ?? slotRoom.roomId ?? slotRoom.RoomId ?? null,
        roomType: slotRoom.roomType ?? slotRoom.roomTypeName ?? slotRoom.roomName ?? null,
        roomTypeName: slotRoom.roomTypeName ?? slotRoom.roomType ?? null,
        board: slotRoom.board ?? null,
        roomSet: slotRoom.roomSet ?? slotRoom.RoomSet ?? null,
        adultFare: slotRoom.adultFare ?? null,
      },
      occupancySlot
    );
    if (slotCanonicalHit) return slotCanonicalHit;
  }

  const labels = resolvePreferredRoomTypeLabelsFromSelectedResult(selectedResult);
  pushUniqueLabel(labels, slotRoom?.roomType);
  pushUniqueLabel(labels, slotRoom?.roomTypeName);
  pushUniqueLabel(labels, slotRoom?.roomName);
  pushUniqueLabel(labels, slotRoom?.RoomType);

  const typeMatchesOccupancy = (row) =>
    !occupancySlot ||
    isRoomRowOccupancyCompatible(row?.roomSuggestion ?? row, occupancySlot);

  for (const label of labels) {
    const exact = pool.find(
      (row) =>
        typeMatchesOccupancy(row) &&
        roomTypeLabelFromSuggestion(row?.roomSuggestion) === label
    );
    if (exact) return exact;
  }

  for (const label of labels) {
    if (label.length < 3) continue;
    const fuzzy = pool.filter((row) => {
      if (!typeMatchesOccupancy(row)) return false;
      const rt = roomTypeLabelFromSuggestion(row?.roomSuggestion);
      if (!rt) return false;
      return rt.includes(label) || label.includes(rt);
    });
    if (fuzzy.length === 1) return fuzzy[0];
  }

  const searchAdultFare = resolveSearchAdultFare(selectedResult);
  if (searchAdultFare != null) {
    const byFare = pool.filter(
      (row) =>
        typeMatchesOccupancy(row) &&
        Number(row?.roomSuggestion?.adultFare ?? row?.roomSuggestion?.AdultFare) ===
          searchAdultFare
    );
    if (byFare.length === 1) return byFare[0];
  }

  // Hard rule: never invent / never pick cheapest.
  // Caller leaves the slot empty (pre-selection) or keeps the existing selection (checkout/payment).
  return null;
}

/**
 * RoomsSelector initial row for a slot.
 * Search-card identity wins; cheapest is never used to override it.
 */
export function resolvePackageProfileInitialRoom({
  availableRoomsSortedCheapestFirst,
  selectedResult,
  slotRoom = null,
  isOrganizedTourDeal = false,
  slotIndex = 0,
  occupancySlot = null,
} = {}) {
  const rooms = Array.isArray(availableRoomsSortedCheapestFirst)
    ? availableRoomsSortedCheapestFirst.filter(Boolean)
    : [];
  const occupancyFiltered = occupancySlot
    ? filterAvailableRoomsForSlotOccupancy(rooms, occupancySlot)
    : rooms;
  if (occupancyFiltered.length === 0) {
    return { row: null, reason: "empty_matrix" };
  }
  const matched = pickAvailableRoomMatchingSearchSelection(
    occupancyFiltered,
    selectedResult,
    slotRoom,
    slotIndex,
    occupancySlot
  );
  if (matched) {
    return { row: matched, reason: "search_card_identity" };
  }
  if (
    hasCanonicalSearchCardRoom(selectedResult) ||
    (slotRoom &&
      (slotRoom["@seqNum"] != null ||
        slotRoom.roomType ||
        slotRoom.roomSet ||
        Number(slotRoom.adultFare) > 0))
  ) {
    return { row: null, reason: "canonical_unmatched_no_cheapest_fallback" };
  }
  if (isOrganizedTourDeal) {
    // Keep the search-card package price. Cheapest extended room (SGL / lower fare)
    // was replacing it as soon as the profile opened.
    return { row: null, reason: "organized_tour_keep_search_price" };
  }
  return { row: null, reason: "vacation_package_no_auto_select" };
}
